import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["renderList", "renderRow", "shareButton", "item"];
import I18n from 'I18n';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UICopyButton from 'UIComponents/button/UICopyButton';
import { buildReferralLink, getLessonShareUrl, getPlaylistUrl, getTrackShareUrl } from '../../../utils/RoutingUtils';
import UsageTracker from '../../../utils/UsageTracker';
import { useUserInfo } from '../../accounts';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILink from 'UIComponents/link/UILink';
import { getShortContentUrl } from '../../shortContent/shortContentRouting';
import { useSocialShareData } from '../socialShareHooks';
import { getShareLinks } from 'academy-common-lib/utils/url';
import { useFullLesson } from '../../lessons';
import { useFullTrack } from '../../tracks';
import getLang from 'academy-common-lib/utils/getLang';
import styled, { theme } from 'academy-common-lib/utils/Styling';
import UIButton from 'UIComponents/button/UIButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const SubmenuButton = styled.div.withConfig({
  displayName: "SocialShareList__SubmenuButton",
  componentId: "sc-1s4onxj-0"
})(["padding:8px 20px;"]);
const SubmenuTitle = styled.div.withConfig({
  displayName: "SocialShareList__SubmenuTitle",
  componentId: "sc-1s4onxj-1"
})(["font-weight:500;color:", ";margin-bottom:-4px;"], theme.colors.OBSIDIAN);
const SubmenuSubtitle = styled.small.withConfig({
  displayName: "SocialShareList__SubmenuSubtitle",
  componentId: "sc-1s4onxj-2"
})(["color:", ";"], theme.colors.SLINKY);
const useReferalLink = ({
  id,
  type,
  language
}) => {
  const {
    userInfo
  } = useUserInfo();
  const userId = Number(userInfo && userInfo.user.id);
  const {
    lesson
  } = useFullLesson(id, language, type !== 'LESSON');
  const {
    track
  } = useFullTrack(id, language || getLang(), type !== 'TRACK');
  switch (type) {
    case 'PLAYLIST':
      return buildReferralLink(getPlaylistUrl(id), userId);
    case 'LESSON':
      return getLessonShareUrl(lesson, userId, id, language);
    case 'TRACK':
      return getTrackShareUrl(track, userId, id, language);
    case 'SHORT_FORM_CONTENT':
      return buildReferralLink(getShortContentUrl({
        shortId: id
      }).fullUrl, userId);
    default:
  }
  return null;
};
const useShareLink = item => {
  const link = useReferalLink(item);
  const {
    template: referalTemplate
  } = useSocialShareData(item);
  const template = referalTemplate ? `${referalTemplate}: ` : '';
  return Object.assign({
    link
  }, getShareLinks(link || '', template));
};
const SocialShareListOption = ({
  onClick,
  href,
  renderRow,
  children
}) => {
  return renderRow(href ? /*#__PURE__*/_jsx(UILink, {
    href: href,
    onClick: onClick,
    target: "_blank",
    use: "unstyled",
    children: children
  }) : children);
};
const getSocialShareListOptions = ({
  item,
  onShare,
  renderRow
}, {
  link,
  linkForFacebook,
  linkForLinkedIn,
  linkForWhatsapp,
  linkForX
}) => {
  const handleOnClick = shareType => {
    UsageTracker.track('interaction', {
      action: 'social-share',
      to: shareType,
      contentId: item.id,
      contentType: item.type,
      playlistId: item.playlistId,
      trackId: item.trackId
    });
    if (onShare) onShare(shareType);
  };
  return [/*#__PURE__*/_jsx(SocialShareListOption, {
    renderRow: renderRow,
    children: /*#__PURE__*/_jsx(UICopyButton, {
      onClick: () => {
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('learningCenter.shareThis.notification')
        });
        handleOnClick('link');
      },
      style: {
        width: '100%'
      },
      tooltipPlacement: "right",
      use: "unstyled",
      value: link,
      children: /*#__PURE__*/_jsxs(UILink, {
        use: "unstyled",
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "copy",
          size: "small"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.socialShare.copyLink"
        })]
      })
    })
  }, "copy-link"), /*#__PURE__*/_jsxs(SocialShareListOption, {
    href: linkForX,
    onClick: () => handleOnClick('x'),
    renderRow: renderRow,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "socialX",
      size: "small"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareX"
    })]
  }, "share-x"), /*#__PURE__*/_jsxs(SocialShareListOption, {
    href: linkForFacebook,
    onClick: () => handleOnClick('facebook'),
    renderRow: renderRow,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "socialFacebook",
      size: 24,
      style: {
        width: 16,
        transform: 'translateX(-5px)',
        marginRight: 7
      }
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareFacebook"
    })]
  }, "share-facebook"), /*#__PURE__*/_jsxs(SocialShareListOption, {
    href: linkForLinkedIn,
    onClick: () => handleOnClick('linkedin'),
    renderRow: renderRow,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "socialLinkedin",
      size: "small"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareLinkedIn"
    })]
  }, "share-linkedin"), /*#__PURE__*/_jsxs(SocialShareListOption, {
    href: linkForWhatsapp,
    onClick: () => handleOnClick('whatsapp'),
    renderRow: renderRow,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "socialWhatsapp",
      size: "small"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareWhatsApp"
    })]
  }, "share-whatsapp")];
};
const SocialShareListSubmenu = ({
  item,
  renderList,
  renderRow,
  onShare
}) => {
  const {
    template: itemName,
    isShareable
  } = useSocialShareData(item);
  const shareLinks = useShareLink(item);
  if (!isShareable) return null;
  return renderList(getSocialShareListOptions({
    item,
    onShare,
    renderRow
  }, shareLinks), /*#__PURE__*/_jsxs(SubmenuButton, {
    children: [/*#__PURE__*/_jsx(SubmenuTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.socialShare.submenu.${item.type}`
      })
    }), /*#__PURE__*/_jsx(SubmenuSubtitle, {
      children: itemName
    })]
  }));
};
export const SocialShareList = _ref => {
  let {
      renderList = content => content,
      renderRow = button => button,
      shareButton = /*#__PURE__*/_jsx(UIButton, {
        size: "sm",
        use: "tertiary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.common.share"
        })
      }),
      item
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    playlistId,
    trackId,
    language
  } = item;
  const shareLinks = useShareLink(item);
  const isContainedInPlaylist = !!playlistId;
  const isContainedInTrack = !!trackId; // lesson within a track has to be public to be shareable

  if (isContainedInPlaylist || isContainedInTrack) {
    // shared item is contained in a playlist or a track, so we need to
    // ask first which item to share (playlist, track or the lesson/short)

    const list = [];
    if (isContainedInPlaylist) {
      list.push( /*#__PURE__*/_createElement(SocialShareListSubmenu, Object.assign({}, props, {
        item: {
          id: playlistId,
          type: 'PLAYLIST'
        },
        key: "share-playlist-submenu",
        renderList: renderList,
        renderRow: renderRow
      })));
    }
    if (isContainedInTrack) {
      list.push( /*#__PURE__*/_createElement(SocialShareListSubmenu, Object.assign({}, props, {
        item: {
          id: trackId,
          language,
          type: 'TRACK'
        },
        key: "share-track-submenu",
        renderList: renderList,
        renderRow: renderRow
      })));
    }
    list.push( /*#__PURE__*/_jsx(SocialShareListSubmenu, Object.assign({
      item: item,
      renderList: renderList,
      renderRow: renderRow
    }, props), "share-item-submenu"));
    return renderList(list, shareButton, true);
  }
  return renderList(getSocialShareListOptions({
    item,
    onShare: props.onShare,
    renderRow
  }, shareLinks), shareButton, true);
};
export default SocialShareList;